import React from 'react';

import classNames from 'classnames';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import ExclamationTriangleIcon from 'Assets/icons/exclamation-triangle.svg';

/**
 * Whether the workforce on screen foots to the stored workforce figure for the
 * same budget version.
 *
 * Four states, all decided server-side — the component never works out whether
 * the comparison is meaningful, because the rule has to travel with the numbers
 * it governs:
 *
 *  - `match`        the roll-up equals the reference. Stated, not celebrated.
 *  - `mismatch`     a real discrepancy. The only place in this view allowed to
 *                   use red (#F00024), which the design reserves for alerts.
 *  - `not_comparable`  a type filter or a compare-to version is active, so the
 *                   reference is measuring a different set. The verdict is
 *                   suppressed rather than failed — a check that cries wolf on
 *                   correct data gets ignored, which is worse than no check.
 *  - `no_reference`  nothing is stored to compare against. Distinct from
 *                   `not_comparable` because the remedy is different: one is
 *                   undone by clearing a control the user set, the other means
 *                   nothing has been calculated for this version yet — which a
 *                   locked/approved version, the case this is mostly reached on
 *                   (PeopleRollupService::reconciliation()), can never do by
 *                   recalculating. The copy therefore states the fact and
 *                   names no remedy, same as `mismatch` below.
 *
 * Every state's copy must be true of that state alone. Telling a user they
 * filtered something they never touched, on the one panel whose job is stating
 * whether the numbers foot, is the worst failure available here — which is why
 * an unrecognised status falls back to copy that names no cause at all rather
 * than to any specific explanation.
 *
 * The whole payload key is absent for a user without pay-rate visibility, in
 * which case nothing renders: every figure here is money.
 */
const NEUTRAL_STATE = {
    Icon: CircleInfoIcon,
    band: 'tw-bg-charcoal-10 tw-border-charcoal-25',
    icon: 'tw-text-charcoal-50',
};

const STATES = {
    match: {
        Icon: CircleCheckIcon,
        band: 'tw-bg-green-10 tw-border-green-50',
        icon: 'tw-text-green',
    },
    mismatch: {
        Icon: ExclamationTriangleIcon,
        band: 'tw-bg-red-10 tw-border-red-50',
        icon: 'tw-text-red',
    },
    not_comparable: NEUTRAL_STATE,
    no_reference: NEUTRAL_STATE,
};

function headline(reconciliation) {
    const total = formatToFixedMoneyWithLocale(reconciliation.rollup_total);

    switch (reconciliation.status) {
        case 'match':
            return $t('Reconciled — {{total}}', { total });
        case 'mismatch':
            return $t('Does not reconcile — {{total}} here against {{reference}} recorded', {
                total,
                reference: formatToFixedMoneyWithLocale(reconciliation.reference),
            });
        case 'not_comparable':
            return $t('Showing a filtered subset — {{total}}', { total });
        case 'no_reference':
            return $t('Nothing recorded to reconcile against — {{total}}', { total });
        default:
            // An unrecognised status must not invent a cause. States only the
            // figure, which is true whatever the status turns out to mean.
            return $t('Workforce total — {{total}}', { total });
    }
}

function detail(reconciliation) {
    const label = reconciliation.reference_label;

    switch (reconciliation.status) {
        case 'match':
            return $t('Matches {{label}}.', { label });
        case 'mismatch': {
            // Absolute value with the direction in words. formatToFixedMoneyWithLocale
            // renders a negative in accounting parentheses, which reads as an aside
            // rather than a shortfall mid-sentence: "(3,000) against …".
            const amount = formatToFixedMoneyWithLocale(Math.abs(reconciliation.difference));

            // States the discrepancy without diagnosing it. The two writers of the
            // recorded figure disagree on whether staff contract rows count — the
            // incremental one excludes them, the full rebuild does not — so
            // "recalculate" is not reliably the remedy and naming it as the cause
            // would send people to the wrong place.
            return reconciliation.difference < 0
                ? $t(
                      '{{amount}} short of {{label}}. The two figures were calculated from different sets — check whether the recorded figure includes staff contract costs.',
                      { amount, label },
                  )
                : $t(
                      '{{amount}} more than {{label}}. The two figures were calculated from different sets — check whether the recorded figure includes staff contract costs.',
                      { amount, label },
                  );
        }
        case 'not_comparable':
            return $t(
                'No verdict against {{label}} while a position type is unticked or a comparison version is selected — the recorded figure covers all types in one version.',
                { label },
            );
        case 'no_reference':
            // No remedy named: this state is reached mainly on locked/approved
            // versions, which BudgetService::isWritableForRecalculation() never
            // lets recalculate — so "recalculate to fix this" would be wrong
            // for exactly the versions the compare-to selector is for.
            return $t('{{label}} has no stored workforce figure for this version.', { label });
        default:
            return $t('No verdict against {{label}}.', { label });
    }
}

export default function PeopleReconciliationStrip({ reconciliation }) {
    if (!reconciliation) {
        return null;
    }

    const state = STATES[reconciliation.status] ?? NEUTRAL_STATE;
    const { Icon } = state;

    return (
        <div className={classNames('tw-flex tw-items-start tw-gap-x-3 tw-px-4 tw-py-3 tw-rounded-lg tw-border-2', state.band)} role="status">
            <Icon className={classNames('tw-w-4 tw-h-4 tw-shrink-0 tw-mt-0.5', state.icon)} />
            <div className="tw-min-w-0">
                <div className="tw-text-sm tw-font-semibold tw-text-charcoal">{headline(reconciliation)}</div>
                <div className="tw-text-xs tw-text-charcoal-75 tw-mt-0.5">{detail(reconciliation)}</div>
            </div>
        </div>
    );
}
